import React, { useState, useEffect } from 'react';
import {
  Modal,
  Table,
  Button,
  Form,
  Select,
  InputNumber,
  Input,
  Space,
  Radio,
  message,
  Popconfirm,
  Typography,
  Row,
  Col,
  Card,
  Tag,
  DatePicker,
  Tooltip
} from 'antd';
import dayjs from 'dayjs';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SaveOutlined,
  CloseOutlined,
  ProductOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  DiffOutlined
} from '@ant-design/icons';
import { customsAPI, supplierAPI } from '../services/api.ts';
import apiClient from '../utils/axiosConfig.ts';
import { generateProductDisplayName } from '../utils/productUtils.ts';
import { formatCurrency } from '../utils/numberFormatting.ts';
import { RectificationReason } from '../types.ts';
import DocumentItemRectifyModal from './DocumentItemRectifyModal.tsx';

const { Option } = Select;
const { Text } = Typography;

interface Product {
  id: number;
  internal_product_code: string;
  product_description: string;
  category: string;
  description_2: string;
  size?: string;
  color?: string;
  dogana_code?: string;
  cost?: number;
  price?: number;
  weight_gross?: number;
}

interface CustomsDocumentItem {
  id: number;
  product_id: number;
  // Riga di spedizione da cui nasce (dichiarazioni generate dalla spedizione)
  shipment_item_id?: number | null;
  // Fattura del fornitore della riga di spedizione (v3.8.0)
  invoice_number?: string | null;
  packing_list_number?: string | null;
  product_code: string;
  product_description: string;
  dogana_code: string | null;
  stock_code: string | null;
  date_stock: string | null;
  original_quantity_loaded: number;
  current_quantity_remaining: number;
  current_quantity_used: number;
  unit_cost: number | null;
  unit_price: number | null;
  singolo: number | null;
  weight_gross: number | null;
  udc_pallet_id: number | null;
  udc_collo_id: number | null;
  udc_pallet: string | null;
  udc_collo: string | null;
  notes: string | null;
  // Situazione bolletta (calcolata dal backend da giacenze e movimenti)
  stock_qty_now?: number;
  stock_rows_count?: number;
  stock_by_warehouse?: { warehouse_id: number | null; warehouse_description: string | null; qty: number; rows: number }[];
  out_qty?: number;
  out_sales?: number;
  out_other?: number;
  out_cleared?: number;   // OUT "Prodotto sdoganato" (Nazionalizza Prodotti)
  adj_out?: number;
  adj_in?: number;
  balance_ok?: boolean;
  balance_diff?: number;
  // Giacenze collegate alla riga ma di un altro ordine fornitore (partita non coerente)
  lot_mismatch?: boolean;
  lot_mismatch_rows?: number;
  lot_mismatch_qty?: number;
}

interface ProductsManagementModalProps {
  visible: boolean;
  onCancel: () => void;
  documentId: number;
  documentStatus: string;
  documentType: string;
  /** Spedizione di origine del documento: se presente, la modifica della quantità in
   *  bozza riallinea spedizione e ordine fornitore (motivo obbligatorio) */
  shipmentId?: number | null;
  onSuccess: () => void;
}

const ProductsManagementModal: React.FC<ProductsManagementModalProps> = ({
  visible,
  onCancel,
  documentId,
  documentStatus,
  documentType,
  shipmentId,
  onSuccess
}) => {
  const [items, setItems] = useState<CustomsDocumentItem[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState<number | null>(null);
  const [addingNew, setAddingNew] = useState(false);
  const [editConfirmed, setEditConfirmed] = useState(false);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  // Rettifica quantità (motore lot_rectification): motivo richiesto in bozza da
  // spedizione, modal dedicato sui documenti elaborati
  const [reasons, setReasons] = useState<RectificationReason[]>([]);
  const [rectifyPrompt, setRectifyPrompt] = useState<{ itemId: number; data: any; oldQty: number; newQty: number; canMove: boolean } | null>(null);
  // v3.9.0: 'move' = sposto pezzi tra dichiarazioni (nessuna differenza reale), 'rectify' = differenza reale
  const [changeKind, setChangeKind] = useState<'move' | 'rectify'>('move');
  const [rectifyForm] = Form.useForm();
  const [rectifyItem, setRectifyItem] = useState<CustomsDocumentItem | null>(null);

  const canEdit = documentStatus === 'draft';
  const canEditProcessed = documentStatus === 'processed';

  useEffect(() => {
    if (visible) {
      loadData();
      setEditConfirmed(false);
      setRectifyPrompt(null);
      setRectifyItem(null);
      supplierAPI.getRectificationReasons().then(setReasons).catch(() => setReasons([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, documentId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [itemsResponse, productsResponse] = await Promise.all([
        customsAPI.getCustomsItems(documentId),
        apiClient.get('/products')
      ]);

      setItems(itemsResponse);
      setProducts(productsResponse.data);
    } catch (error) {
      message.error('Errore nel caricamento dei dati');
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddProduct = async (values: any) => {
    try {
      const data = {
        product_id: values.product_id,
        original_quantity_loaded: values.quantity,
        unit_cost: values.unit_cost,
        unit_price: values.unit_price,
        singolo: values.singolo,
        weight_gross: values.weight_gross,
        udc_pallet: values.udc_pallet,
        udc_collo: values.udc_collo,
        dogana_code: values.dogana_code || null,
        stock_code: values.stock_code || null,
        date_stock: values.date_stock ? values.date_stock.format('YYYY-MM-DD HH:mm:ss') : null,
        notes: values.notes
      };

      await customsAPI.createCustomsItem(documentId, data);
      message.success('Prodotto aggiunto con successo');

      setAddingNew(false);
      form.resetFields();
      loadData();
      onSuccess();
    } catch (error: any) {
      console.error('Error adding product:', error);
      if (error.response?.data?.detail) {
        message.error(`Errore: ${error.response.data.detail}`);
      } else {
        message.error('Errore durante l\'aggiunta del prodotto');
      }
    }
  };

  const submitUpdateProduct = async (itemId: number, data: any) => {
    try {
      const result = await customsAPI.updateCustomsItem(documentId, itemId, data);
      message.success(result?.message || 'Prodotto aggiornato con successo');

      setRectifyPrompt(null);
      rectifyForm.resetFields();
      setEditingItem(null);
      editForm.resetFields();
      loadData();
      onSuccess();
    } catch (error: any) {
      console.error('Error updating product:', error);
      if (error.response?.data?.detail) {
        message.error(`Errore: ${error.response.data.detail}`);
      } else {
        message.error('Errore durante l\'aggiornamento del prodotto');
      }
    }
  };

  const handleUpdateProduct = async (itemId: number, values: any) => {
    try {
      const data = {
        original_quantity_loaded: values.quantity,
        unit_cost: values.unit_cost,
        unit_price: values.unit_price,
        singolo: values.singolo,
        weight_gross: values.weight_gross,
        udc_pallet: values.udc_pallet,
        udc_collo: values.udc_collo,
        dogana_code: values.dogana_code || null,
        stock_code: values.stock_code || null,
        date_stock: values.date_stock ? values.date_stock.format('YYYY-MM-DD HH:mm:ss') : null,
        notes: values.notes
      };

      // Documento doganale (IM7/IM4) nato da spedizione: cambiare la quantità in bozza
      // riallinea la riga di spedizione e l'ordine fornitore (motore di rettifica) →
      // serve il motivo. DDT/Packing List/Altro sono arrivi (anche parziali): la
      // quantità si corregge entro la merce in transito, senza rettificare la partita.
      const current = items.find((it) => it.id === itemId);
      const isCustomsDoc = documentType === 'IM7' || documentType === 'IM4';
      if (shipmentId && isCustomsDoc && current && values.quantity !== current.original_quantity_loaded) {
        const canMove = !!current.shipment_item_id;
        setRectifyPrompt({ itemId, data, oldQty: current.original_quantity_loaded, newQty: values.quantity, canMove });
        setChangeKind(canMove ? 'move' : 'rectify');
        rectifyForm.resetFields();
        return;
      }

      await submitUpdateProduct(itemId, data);
    } catch (error: any) {
      console.error('Error updating product:', error);
      if (error.response?.data?.detail) {
        message.error(`Errore: ${error.response.data.detail}`);
      } else {
        message.error('Errore durante l\'aggiornamento del prodotto');
      }
    }
  };

  const handleDeleteProduct = async (itemId: number) => {
    try {
      await customsAPI.deleteCustomsItem(documentId, itemId);
      message.success('Prodotto rimosso con successo');

      loadData();
      onSuccess();
    } catch (error: any) {
      console.error('Error deleting product:', error);
      if (error.response?.data?.detail) {
        message.error(`Errore: ${error.response.data.detail}`);
      } else {
        message.error('Errore durante la rimozione del prodotto');
      }
    }
  };

  const handleUpdateProcessedValues = async (itemId: number, values: any) => {
    try {
      const data: { unit_cost?: number, unit_price?: number, weight_gross?: number, stock_code?: string | null } = {};
      if (values.unit_cost !== undefined) data.unit_cost = values.unit_cost;
      if (values.unit_price !== undefined) data.unit_price = values.unit_price;
      if (values.weight_gross !== undefined) data.weight_gross = values.weight_gross;
      // Stock Code inviato solo se cambiato: il server lo propaga alle giacenze della riga
      const original = items.find(i => i.id === itemId);
      const normCode = (v: string | null | undefined) => (v || '').trim().toUpperCase();
      if (values.stock_code !== undefined && normCode(values.stock_code) !== normCode(original?.stock_code)) {
        data.stock_code = values.stock_code || null;
      }

      const result = await customsAPI.updateProcessedItemValues(documentId, itemId, data);
      message.success(result.message || 'Valori aggiornati con successo');

      setEditingItem(null);
      editForm.resetFields();
      loadData();
      onSuccess();
    } catch (error: any) {
      console.error('Error updating processed item values:', error);
      if (error.response?.data?.detail) {
        message.error(`Errore: ${error.response.data.detail}`);
      } else {
        message.error('Errore durante l\'aggiornamento dei valori');
      }
    }
  };

  const startEdit = (item: CustomsDocumentItem) => {
    setEditingItem(item.id);
    editForm.setFieldsValue({
      quantity: item.original_quantity_loaded,
      unit_cost: item.unit_cost,
      unit_price: item.unit_price,
      singolo: item.singolo,
      weight_gross: item.weight_gross,
      udc_pallet: item.udc_pallet,
      udc_collo: item.udc_collo,
      dogana_code: item.dogana_code,
      stock_code: item.stock_code,
      date_stock: item.date_stock ? dayjs(item.date_stock) : null,
      notes: item.notes
    });
  };

  const startEditProcessed = (item: CustomsDocumentItem) => {
    if (editConfirmed) {
      startEdit(item);
      return;
    }
    Modal.confirm({
      title: 'Attenzione',
      icon: <ExclamationCircleOutlined />,
      content: 'La modifica di costo, prezzo, peso lordo e Stock Code verrà applicata anche all\'inventario per tutti i record di stock collegati a questa riga del documento. Vuoi procedere?',
      okText: 'Sì, procedi',
      cancelText: 'Annulla',
      onOk() {
        setEditConfirmed(true);
        startEdit(item);
      },
    });
  };

  const cancelEdit = () => {
    setEditingItem(null);
    editForm.resetFields();
  };

  const handleClose = () => {
    if (editingItem !== null) {
      Modal.confirm({
        title: 'Modifiche non salvate',
        icon: <ExclamationCircleOutlined />,
        content: 'Hai delle modifiche in corso non salvate. Vuoi salvarle prima di chiudere?',
        okText: 'Salva e chiudi',
        cancelText: 'Chiudi senza salvare',
        onOk() {
          editForm.validateFields().then((values) => {
            const savePromise = canEditProcessed
              ? handleUpdateProcessedValues(editingItem, values)
              : handleUpdateProduct(editingItem, values);
            savePromise.then(() => onCancel());
          }).catch((err) => {
            console.error('Validation failed:', err);
          });
        },
        onCancel() {
          cancelEdit();
          onCancel();
        },
      });
    } else {
      onCancel();
    }
  };

  const cancelAdd = () => {
    setAddingNew(false);
    form.resetFields();
  };

  const getTotalQuantity = () => {
    return items.reduce((sum, item) => sum + item.original_quantity_loaded, 0);
  };

  const getTotalValue = () => {
    return items.reduce((sum, item) =>
      sum + (item.original_quantity_loaded * (item.unit_cost || 0)), 0
    );
  };

  // Situazione bolletta: ha senso solo per documenti elaborati (in bozza nulla è entrato)
  const isProcessed = documentStatus !== 'draft';
  const getTotalInStock = () => items.reduce((sum, item) => sum + (item.stock_qty_now || 0), 0);
  const getTotalOut = () => items.reduce((sum, item) => sum + (item.out_qty || 0), 0);
  const unbalancedCount = items.filter(item => item.balance_ok === false).length;
  const lotMismatchCount = items.filter(item => item.lot_mismatch).length;

  const balanceTooltip = (record: CustomsDocumentItem) => {
    const loaded = record.original_quantity_loaded || 0;
    const now = record.stock_qty_now || 0;
    const out = record.out_qty || 0;
    const diff = record.balance_diff || 0;
    const adj = (record.adj_out || record.adj_in)
      ? `, rettifiche manuali ${record.adj_out ? `-${record.adj_out}` : ''}${record.adj_out && record.adj_in ? ' / ' : ''}${record.adj_in ? `+${record.adj_in}` : ''}`
      : '';
    return `Caricati ${loaded}, in magazzino ${now}, usciti ${out}${adj}: differenza ${diff > 0 ? '+' : ''}${diff}. ` +
      'La merce di questa bolletta è stata fusa con altre partite o le uscite non sono state tutte registrate.';
  };

  const columns = [
    {
      title: 'Singolo',
      dataIndex: 'singolo',
      key: 'singolo',
      width: 80,
      render: (value: number | null, record: CustomsDocumentItem) => {
        if (editingItem === record.id && canEdit) {
          return (
            <Form.Item name="singolo" style={{ margin: 0 }}>
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                placeholder="Opz."
              />
            </Form.Item>
          );
        }
        return value !== null ? value : '-';
      },
    },
    {
      title: 'Codice Doganale',
      dataIndex: 'dogana_code',
      key: 'dogana_code',
      width: 140,
      render: (value: string | null, record: CustomsDocumentItem) => {
        if (editingItem === record.id && canEdit) {
          return (
            <Form.Item name="dogana_code" style={{ margin: 0 }}>
              <Input
                placeholder="Cod. doganale"
                allowClear
                style={{ width: '100%' }}
              />
            </Form.Item>
          );
        }
        return value || '-';
      },
    },
    {
      title: 'Prodotto',
      dataIndex: 'product_code',
      key: 'product_code',
      render: (code: string, record: CustomsDocumentItem) => (
        <div>
          <Text strong>{code}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.product_description}
          </Text>
          {record.invoice_number && (
            <>
              <br />
              <Text type="secondary" style={{ fontSize: '11px' }}>
                Fattura {record.invoice_number}{record.packing_list_number ? ` - PL ${record.packing_list_number}` : ''}
              </Text>
            </>
          )}
        </div>
      ),
    },
    {
      title: 'Quantità',
      dataIndex: 'original_quantity_loaded',
      key: 'quantity',
      width: 120,
      render: (quantity: number, record: CustomsDocumentItem) => {
        if (editingItem === record.id && canEdit) {
          return (
            <Form.Item name="quantity" style={{ margin: 0 }}>
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
          );
        }
        return <Text strong>{quantity}</Text>;
      },
    },
    {
      title: 'In Magazzino',
      dataIndex: 'stock_qty_now',
      key: 'stock_qty_now',
      width: 110,
      render: (value: number | undefined, record: CustomsDocumentItem) => {
        if (!isProcessed) return '-';
        const byWarehouse = record.stock_by_warehouse || [];
        const tooltip = byWarehouse.length > 0
          ? byWarehouse.map(w => `${w.warehouse_description || 'Magazzino n.d.'}: ${w.qty} (${w.rows} righe)`).join(' | ')
          : 'Nessuna giacenza collegata a questa bolletta';
        return (
          <Space size={4}>
            <Tooltip title={tooltip}>
              <Text strong={!!value} type={value ? undefined : 'secondary'}>{value ?? 0}</Text>
            </Tooltip>
            {record.lot_mismatch && (
              <Tooltip title={`Partita non coerente: ${record.lot_mismatch_rows} giacenze collegate a questa riga (${record.lot_mismatch_qty} pz disponibili) appartengono a un altro ordine fornitore. In Magazzino e Usciti della riga contano merce di un'altra partita.`}>
                <WarningOutlined style={{ color: '#f5222d' }} />
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Usciti',
      dataIndex: 'out_qty',
      key: 'out_qty',
      width: 100,
      render: (value: number | undefined, record: CustomsDocumentItem) => {
        if (!isProcessed) return '-';
        const adjParts: string[] = [];
        if (record.adj_out) adjParts.push(`-${record.adj_out}`);
        if (record.adj_in) adjParts.push(`+${record.adj_in}`);
        const tooltip = `Vendite: ${record.out_sales || 0} | Sdoganati: ${record.out_cleared || 0} | Altre uscite: ${record.out_other || 0}`
          + (adjParts.length ? ` | Rettifiche manuali: ${adjParts.join(' / ')}` : '');
        return (
          <Tooltip title={tooltip}>
            <Space size={4}>
              <Text type={value ? undefined : 'secondary'}>{value ?? 0}</Text>
              {record.balance_ok === false && (
                <Tooltip title={balanceTooltip(record)}>
                  <WarningOutlined style={{ color: '#fa8c16' }} />
                </Tooltip>
              )}
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: 'Costo Unit.',
      dataIndex: 'unit_cost',
      key: 'unit_cost',
      width: 150,
      render: (cost: number | null, record: CustomsDocumentItem) => {
        if (editingItem === record.id && (canEdit || canEditProcessed)) {
          return (
            <Form.Item name="unit_cost" style={{ margin: 0 }}>
              <InputNumber
                min={0}
                step={0.01}
                decimalSeparator=","
                style={{ width: '100%' }}
                addonAfter="€"
              />
            </Form.Item>
          );
        }
        return cost ? `€${formatCurrency(cost)}` : '-';
      },
    },
    {
      title: 'Vendita Unit.',
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 150,
      render: (price: number | null, record: CustomsDocumentItem) => {
        if (editingItem === record.id && (canEdit || canEditProcessed)) {
          return (
            <Form.Item name="unit_price" style={{ margin: 0 }}>
              <InputNumber
                min={0}
                step={0.01}
                decimalSeparator=","
                style={{ width: '100%' }}
                addonAfter="€"
              />
            </Form.Item>
          );
        }
        return price ? `€${formatCurrency(price)}` : '-';
      },
    },
    {
      title: 'Peso Lordo',
      dataIndex: 'weight_gross',
      key: 'weight_gross',
      width: 160,
      render: (value: number | null, record: CustomsDocumentItem) => {
        if (editingItem === record.id && (canEdit || canEditProcessed)) {
          return (
            <Form.Item name="weight_gross" style={{ margin: 0 }}>
              <InputNumber
                min={0}
                step={0.0001}
                precision={4}
                decimalSeparator=","
                style={{ width: '100%' }}
                addonAfter="kg"
                placeholder="Opz."
              />
            </Form.Item>
          );
        }
        return value !== null && value !== undefined ? `${value.toLocaleString('it-IT', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} kg` : '-';
      },
    },
    {
      title: 'UDC Pallet',
      dataIndex: 'udc_pallet',
      key: 'udc_pallet',
      width: 120,
      render: (code: string | null, record: CustomsDocumentItem) => {
        if (editingItem === record.id && canEdit) {
          return (
            <Form.Item name="udc_pallet" style={{ margin: 0 }}>
              <Input
                placeholder="Codice pallet"
                allowClear
                style={{ width: '100%' }}
              />
            </Form.Item>
          );
        }
        return code ? <Text>{code}</Text> : '-';
      },
    },
    {
      title: 'UDC Collo',
      dataIndex: 'udc_collo',
      key: 'udc_collo',
      width: 120,
      render: (code: string | null, record: CustomsDocumentItem) => {
        if (editingItem === record.id && canEdit) {
          return (
            <Form.Item name="udc_collo" style={{ margin: 0 }}>
              <Input
                placeholder="Codice collo"
                allowClear
                style={{ width: '100%' }}
              />
            </Form.Item>
          );
        }
        return code ? <Text>{code}</Text> : '-';
      },
    },
    {
      title: 'Stock Code',
      dataIndex: 'stock_code',
      key: 'stock_code',
      width: 120,
      render: (value: string | null, record: CustomsDocumentItem) => {
        if (editingItem === record.id && (canEdit || canEditProcessed)) {
          return (
            <Form.Item name="stock_code" style={{ margin: 0 }}>
              <Input
                placeholder="Stock code"
                allowClear
                style={{ width: '100%' }}
              />
            </Form.Item>
          );
        }
        return value ? <Text>{value}</Text> : '-';
      },
    },
    {
      title: 'Data Stock',
      dataIndex: 'date_stock',
      key: 'date_stock',
      width: 140,
      render: (value: string | null, record: CustomsDocumentItem) => {
        if (editingItem === record.id && canEdit) {
          return (
            <Form.Item name="date_stock" style={{ margin: 0 }}>
              <DatePicker
                format="DD/MM/YYYY"
                style={{ width: '100%' }}
                placeholder="Opz."
              />
            </Form.Item>
          );
        }
        return value ? dayjs(value).format('DD/MM/YYYY') : '-';
      },
    },
    {
      title: 'Note',
      dataIndex: 'notes',
      key: 'notes',
      width: 150,
      render: (notes: string | null, record: CustomsDocumentItem) => {
        if (editingItem === record.id && canEdit) {
          return (
            <Form.Item name="notes" style={{ margin: 0 }} className="notes-field">
              <Input placeholder="Note opzionali" />
            </Form.Item>
          );
        }
        return notes ? <Text>{notes}</Text> : '-';
      },
    },
    {
      title: 'Azioni',
      key: 'actions',
      width: 120,
      render: (_: any, record: CustomsDocumentItem) => {
        if (!canEdit && !canEditProcessed) {
          return <Text type="secondary">-</Text>;
        }

        if (editingItem === record.id) {
          return (
            <Space size="small">
              <Button
                type="text"
                size="small"
                icon={<SaveOutlined />}
                onClick={() => {
                  editForm.validateFields().then((values) => {
                    if (canEditProcessed) {
                      handleUpdateProcessedValues(record.id, values);
                    } else {
                      handleUpdateProduct(record.id, values);
                    }
                  }).catch((err) => {
                    console.error('Validation failed:', err);
                  });
                }}
                title="Salva"
              />
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onClick={cancelEdit}
                title="Annulla"
              />
            </Space>
          );
        }

        if (canEditProcessed) {
          return (
            <Space size="small">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => startEditProcessed(record)}
                title="Modifica valori"
              />
              <Button
                type="text"
                size="small"
                icon={<DiffOutlined />}
                onClick={() => setRectifyItem(record)}
                title="Rettifica quantità (merce reale diversa da quella caricata)"
              />
            </Space>
          );
        }

        return (
          <Space size="small">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => startEdit(record)}
              title="Modifica"
            />
            <Popconfirm
              title="Rimuovere questo prodotto?"
              onConfirm={() => handleDeleteProduct(record.id)}
              okText="Sì"
              cancelText="No"
            >
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                title="Rimuovi"
              />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <Modal
      title={
        <Space>
          <ProductOutlined />
          <span>Gestione Prodotti - Documento {documentType}</span>
          <Tag color={documentStatus === 'draft' ? 'orange' : 'green'}>
            {documentStatus === 'draft' ? 'Bozza' : 'Elaborato'}
          </Tag>
        </Space>
      }
      open={visible}
      onCancel={handleClose}
      width="98vw"
      style={{ top: 20 }}
      styles={{ body: { maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' } }}
      footer={[
        <Button key="close" onClick={handleClose}>
          Chiudi
        </Button>
      ]}
    >
      {/* Motivo della rettifica: quantità cambiata su una riga in bozza nata da spedizione */}
      <Modal
        title="Motivo della rettifica quantità"
        open={!!rectifyPrompt}
        onCancel={() => { setRectifyPrompt(null); rectifyForm.resetFields(); }}
        okText="Conferma"
        cancelText="Annulla"
        onOk={() => {
          if (rectifyPrompt && rectifyPrompt.canMove && changeKind === 'move') {
            submitUpdateProduct(rectifyPrompt.itemId, { ...rectifyPrompt.data });
            return;
          }
          rectifyForm.validateFields().then((v) => {
            if (!rectifyPrompt) return;
            submitUpdateProduct(rectifyPrompt.itemId, {
              ...rectifyPrompt.data,
              rectification_reason_code: v.reason_code,
              rectification_notes: v.notes?.trim() || undefined
            });
          }).catch(() => undefined);
        }}
        destroyOnClose
      >
        <p>
          La quantità passa da <strong>{rectifyPrompt?.oldQty}</strong> a <strong>{rectifyPrompt?.newQty}</strong> pz.
        </p>
        {rectifyPrompt?.canMove && (
          <Radio.Group value={changeKind} onChange={(e) => setChangeKind(e.target.value)} style={{ marginBottom: 12 }}>
            <Space direction="vertical">
              <Radio value="move">
                Sposto pezzi tra dichiarazioni (nessuna differenza): {(rectifyPrompt?.newQty ?? 0) < (rectifyPrompt?.oldQty ?? 0)
                  ? 'i pezzi tolti tornano da dichiarare sulla spedizione'
                  : 'si aggiungono pezzi ancora da dichiarare della stessa riga'}
              </Radio>
              <Radio value="rectify">Differenza reale (conteggio diverso): rettifica della partita</Radio>
            </Space>
          </Radio.Group>
        )}
        {(!rectifyPrompt?.canMove || changeKind === 'rectify') && (
        <p>
          La riga di spedizione, la merce in transito e la riga dell'ordine fornitore verranno riallineate
          insieme (rettifica della partita).
        </p>
        )}
        {(!rectifyPrompt?.canMove || changeKind === 'rectify') && (
        <Form form={rectifyForm} layout="vertical">
          <Form.Item name="reason_code" label="Motivo" rules={[{ required: true, message: 'Indica il motivo' }]}>
            <Select placeholder="Seleziona il motivo">
              {reasons.map((r) => <Option key={r.code} value={r.code}>{r.label}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="notes" label="Note (opzionali)">
            <Input.TextArea rows={2} className="no-uppercase-modal" />
          </Form.Item>
        </Form>
        )}
      </Modal>

      {/* Rettifica quantità su documento elaborato */}
      <DocumentItemRectifyModal
        visible={!!rectifyItem}
        documentId={documentId}
        item={rectifyItem}
        onCancel={() => setRectifyItem(null)}
        onSuccess={() => {
          setRectifyItem(null);
          loadData();
          onSuccess();
        }}
      />

      {/* Statistics Card */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={isProcessed ? 4 : 8}>
            <Text strong>Prodotti Totali: </Text>
            <Text>{items.length}</Text>
          </Col>
          <Col span={isProcessed ? 4 : 8}>
            <Text strong>Quantità Totale: </Text>
            <Text>{getTotalQuantity()}</Text>
          </Col>
          <Col span={isProcessed ? 5 : 8}>
            <Text strong>Valore Totale: </Text>
            <Text>€ {formatCurrency(getTotalValue())}</Text>
          </Col>
          {isProcessed && (
            <>
              <Col span={4}>
                <Text strong>In Magazzino: </Text>
                <Text>{getTotalInStock()}</Text>
              </Col>
              <Col span={3}>
                <Text strong>Usciti: </Text>
                <Text>{getTotalOut()}</Text>
              </Col>
              <Col span={4}>
                {unbalancedCount > 0 ? (
                  <Tooltip title="Per queste righe caricato ≠ in magazzino + usciti: vedi l'icona nella colonna Usciti">
                    <Tag color="orange" icon={<WarningOutlined />}>
                      {unbalancedCount} {unbalancedCount === 1 ? 'riga non quadra' : 'righe non quadrano'}
                    </Tag>
                  </Tooltip>
                ) : (
                  <Tag color="green">Quadratura OK</Tag>
                )}
                {lotMismatchCount > 0 && (
                  <Tooltip title="Giacenze collegate a righe di un'altra partita: vedi l'icona rossa nella colonna In Magazzino">
                    <Tag color="red" icon={<WarningOutlined />}>
                      {lotMismatchCount} {lotMismatchCount === 1 ? 'riga con partita non coerente' : 'righe con partita non coerente'}
                    </Tag>
                  </Tooltip>
                )}
              </Col>
            </>
          )}
        </Row>
      </Card>

      {/* Add New Product Form */}
      {addingNew && canEdit && (
        <Card size="small" style={{ marginBottom: 16, backgroundColor: '#f6ffed' }}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleAddProduct}
          >
            <Row gutter={16}>
              <Col span={10}>
                <Form.Item
                  name="product_id"
                  label="Prodotto"
                  rules={[{ required: true, message: 'Seleziona un prodotto' }]}
                >
                  <Select
                    showSearch
                    placeholder="Seleziona prodotto"
                    optionFilterProp="children"
                    filterOption={(input, option: any) =>
                      option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                    onChange={(productId) => {
                      const selectedProduct = products.find(p => p.id === productId);
                      form.setFieldsValue({
                        dogana_code: selectedProduct?.dogana_code || '',
                        unit_cost: selectedProduct?.cost ?? null,
                        unit_price: selectedProduct?.price ?? null,
                        weight_gross: selectedProduct?.weight_gross ?? null
                      });
                    }}
                  >
                    {products.map(product => (
                      <Option key={product.id} value={product.id}>
                        {generateProductDisplayName(
                          product.internal_product_code,
                          product.size,
                          product.color,
                          product.product_description
                        )}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={3}>
                <Form.Item
                  name="quantity"
                  label="Quantità"
                  rules={[{ required: true, message: 'Inserisci quantità' }]}
                >
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={3}>
                <Form.Item
                  name="unit_cost"
                  label="Costo Unit."
                >
                  <InputNumber
                    min={0}
                    step={0.01}
                    style={{ width: '100%' }}
                    addonAfter="€"
                  />
                </Form.Item>
              </Col>
              <Col span={3}>
                <Form.Item
                  name="unit_price"
                  label="Vendita Unit."
                >
                  <InputNumber
                    min={0}
                    step={0.01}
                    style={{ width: '100%' }}
                    addonAfter="€"
                  />
                </Form.Item>
              </Col>
              <Col span={3}>
                <Form.Item
                  name="singolo"
                  label="Singolo"
                >
                  <InputNumber
                    min={0}
                    style={{ width: '100%' }}
                    placeholder="Opzionale"
                  />
                </Form.Item>
              </Col>
              <Col span={3}>
                <Form.Item
                  name="weight_gross"
                  label="Peso Lordo"
                >
                  <InputNumber
                    min={0}
                    step={0.0001}
                    precision={4}
                    style={{ width: '100%' }}
                    addonAfter="kg"
                    placeholder="Opzionale"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item
                  name="udc_pallet"
                  label="Pallet"
                >
                  <Input
                    placeholder="Codice"
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="udc_collo"
                  label="Collo"
                >
                  <Input
                    placeholder="Codice"
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="dogana_code"
                  label="Codice Doganale"
                >
                  <Input
                    placeholder="Cod. doganale (opz.)"
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="stock_code"
                  label="Stock Code"
                >
                  <Input
                    placeholder="Stock code (opz.)"
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="date_stock"
                  label="Data Stock"
                >
                  <DatePicker
                    format="DD/MM/YYYY"
                    style={{ width: '100%' }}
                    placeholder="Opzionale"
                  />
                </Form.Item>
              </Col>
              <Col span={16}>
                <Form.Item
                  name="notes"
                  label="Note"
                  className="notes-field"
                >
                  <Input placeholder="Note opzionali" />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <Space>
                  <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                    Aggiungi Prodotto
                  </Button>
                  <Button onClick={cancelAdd}>
                    Annulla
                  </Button>
                </Space>
              </Col>
            </Row>
          </Form>
        </Card>
      )}

      {/* Add Product Button */}
      {!addingNew && canEdit && (
        <div style={{ marginBottom: 16 }}>
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => setAddingNew(true)}
            style={{ width: '100%' }}
          >
            Aggiungi Nuovo Prodotto
          </Button>
        </div>
      )}

      {/* Products Table - wrapped in Form for edit mode */}
      <Form form={editForm}>
        <Table
          columns={columns}
          dataSource={items}
          rowKey="id"
          loading={loading}
          pagination={false}
          scroll={{ x: 1900, y: 'calc(100vh - 480px)' }}
          size="small"
        />
      </Form>

      {canEditProcessed && (
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">
            ℹ️ Questo documento è stato elaborato. È possibile modificare solo Costo, Prezzo, Peso Lordo e Stock Code. Le modifiche verranno applicate anche all'inventario.
          </Text>
        </div>
      )}

      {!canEdit && !canEditProcessed && (
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">
            ℹ️ Questo documento è già stato elaborato e ha impattato l'inventario. Non è possibile modificare i prodotti.
          </Text>
        </div>
      )}

      {canEdit && (
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">
            💡 I prodotti possono essere modificati liberamente fino all'elaborazione del documento.
            L'inventario verrà aggiornato solo quando il documento verrà elaborato tramite il pulsante "Elabora".
          </Text>
        </div>
      )}
    </Modal>
  );
};

export default ProductsManagementModal;